import { useState } from "react";
import { api } from "../api";
import { useAsync } from "../hooks";
import { fmt } from "../format";
import { CopyButton } from "./CopyButton";
import { useConfirmAction } from "./useConfirmAction";

type KeyScope = "full" | "read";
interface ApiKey {
  id: string;
  name: string;
  keyPrefix: string;
  status: "active" | "revoked";
  scope: KeyScope;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}
interface CreatedKey {
  id: string;
  name: string;
  prefix: string;
  scope: KeyScope;
  key: string;
  expiresAt?: string | null;
  /** Present when the key was minted by a ROTATION (the old key lapses after graceHours). */
  graceHours?: number;
}

/** Expiry presets offered on create. 0 = never (the default). */
const EXPIRY_OPTIONS: { label: string; days: number }[] = [
  { label: "No expiry", days: 0 },
  { label: "Expires in 30 days", days: 30 },
  { label: "Expires in 90 days", days: 90 },
  { label: "Expires in 1 year", days: 365 },
];

function isExpired(expiresAt: string | null): boolean {
  return expiresAt != null && new Date(expiresAt).getTime() <= Date.now();
}

export function ApiKeys() {
  const { data, loading, error, reload } = useAsync<ApiKey[]>(() => api("/api-keys"), []);
  const [name, setName] = useState("");
  const [scope, setScope] = useState<KeyScope>("full");
  const [expiresInDays, setExpiresInDays] = useState(0);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedKey | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const confirmKey = useConfirmAction();

  async function create() {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api<CreatedKey>("/api-keys", {
        method: "POST",
        body: JSON.stringify({
          name: n,
          scope,
          ...(expiresInDays > 0 ? { expiresInDays } : {}),
        }),
      });
      setCreated(res);
      setName("");
      reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "failed to create key");
    } finally {
      setBusy(false);
    }
  }

  function rotate(k: ApiKey) {
    confirmKey.ask({
      title: `Rotate "${k.name}"?`,
      body: "A fresh secret is issued now; the current key keeps working for 24h, then stops. Update your clients within that window.",
      confirmLabel: "Rotate",
      run: async () => {
        const res = await api<CreatedKey>(`/api-keys/${k.id}/rotate`, { method: "POST" });
        setCreated(res);
        reload();
      },
    });
  }

  function revoke(id: string) {
    confirmKey.ask({
      title: "Revoke this key?",
      body: "Any client using it stops working immediately.",
      confirmLabel: "Revoke",
      danger: true,
      run: async () => {
        await api(`/api-keys/${id}`, { method: "DELETE" });
        reload();
      },
    });
  }

  const keys = data ?? [];

  return (
    <div className="stack">
      <div className="section-head">
        <h1>API keys</h1>
        <span className="muted">
          Programmatic access to your scrapers. Send as `Authorization: Bearer &lt;key&gt;`.
        </span>
      </div>

      <section className="card">
        <div className="row">
          <input
            placeholder="key name (e.g. production)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void create()}
          />
          <select
            aria-label="key scope"
            value={scope}
            onChange={(e) => setScope(e.target.value as KeyScope)}
          >
            <option value="full">Full access</option>
            <option value="read">Read-only</option>
          </select>
          <select
            aria-label="key expiry"
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(Number(e.target.value))}
          >
            {EXPIRY_OPTIONS.map((o) => (
              <option key={o.days} value={o.days}>
                {o.label}
              </option>
            ))}
          </select>
          <button className="btn" onClick={() => void create()} disabled={busy}>
            {busy ? "Creating…" : "+ Create key"}
          </button>
        </div>
        <div className="muted mt sm">
          {scope === "read"
            ? "Read-only: this key can fetch data (GET) but cannot create, edit, or delete anything — hand it to a data consumer."
            : "Full access: this key can read and write everything in your workspace."}
        </div>
        {err && <div className="err mt">{err}</div>}
        {created && (
          <div className="keycard mt">
            <p>
              <strong>{created.name}</strong> <ScopePill scope={created.scope} /> — copy this now, it
              is never shown again:
            </p>
            <code className="keyval mono">{created.key}</code>
            {created.graceHours != null && (
              <div className="muted sm mt">
                Rotated — the previous key stops working in {created.graceHours}h. Update your
                clients before then.
              </div>
            )}
            {created.graceHours == null && created.expiresAt && (
              <div className="muted sm mt">Expires {fmt(created.expiresAt)}.</div>
            )}
            <div className="row-gap mt">
              <CopyButton text={created.key} label="Copy key" />
              <button className="btn sm ghost" onClick={() => setCreated(null)}>
                Done
              </button>
            </div>
          </div>
        )}
      </section>

      {loading && !data && <div className="muted pad">Loading…</div>}
      {error && <div className="empty">{error}</div>}
      {!loading && keys.length === 0 && (
        <div className="empty">No API keys yet — create one for programmatic access.</div>
      )}
      {keys.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Scope</th>
              <th>Prefix</th>
              <th>Status</th>
              <th>Created</th>
              <th>Last used</th>
              <th>Expires</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => {
              const expired = isExpired(k.expiresAt);
              return (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td>
                    <ScopePill scope={k.scope} />
                  </td>
                  <td>
                    <code className="mono">{k.keyPrefix}…</code>
                  </td>
                  <td>{k.status}</td>
                  <td>{fmt(k.createdAt)}</td>
                  <td>{k.lastUsedAt ? fmt(k.lastUsedAt) : "—"}</td>
                  <td>
                    {k.expiresAt ? (
                      expired ? (
                        <span className="pill failed">expired</span>
                      ) : (
                        fmt(k.expiresAt)
                      )
                    ) : (
                      <span className="muted">never</span>
                    )}
                  </td>
                  <td>
                    {k.status === "active" && !expired && (
                      <div className="row-gap">
                        <button className="btn sm ghost" onClick={() => void rotate(k)}>
                          Rotate
                        </button>
                        <button className="btn sm danger" onClick={() => void revoke(k.id)}>
                          Revoke
                        </button>
                      </div>
                    )}
                    {k.status === "active" && expired && (
                      <button className="btn sm danger" onClick={() => void revoke(k.id)}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {confirmKey.modal}
    </div>
  );
}

/** Read-only keys are drawn as a constrained (dashed) state; full is the quiet default. */
function ScopePill({ scope }: { scope: KeyScope }) {
  return scope === "read" ? (
    <span className="pill hold">read-only</span>
  ) : (
    <span className="muted">full</span>
  );
}
